import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Bot } from "lucide-react";
import { postChat, getChatHistory } from "../lib/api";

const SUGGESTIONS = [
  "Explain P/E ratio simply",
  "Is my portfolio too risky?",
  "What is a step-up SIP?",
  "Difference between STCG and LTCG?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState("claude");
  const endRef = useRef(null);

  useEffect(() => { if (open && msgs.length === 0) getChatHistory().then((d) => setMsgs(d.messages || [])).catch(() => {}); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const d = await postChat({ message: q, provider });
      setMsgs(d.messages);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <motion.button data-testid="chat-fab" onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: "linear-gradient(135deg,#00e5ff,#818cf8)", boxShadow: "0 0 24px rgba(0,229,255,0.4)" }}>
        {open ? <X className="w-6 h-6 text-[#06121a]" /> : <MessageCircle className="w-6 h-6 text-[#06121a]" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[75vh] ao-card flex flex-col overflow-hidden" data-testid="chat-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1e293b]" style={{ background: "rgba(0,229,255,0.06)" }}>
              <div className="flex items-center gap-2"><Bot className="w-5 h-5 text-[#00e5ff]" /><div><div className="text-sm font-semibold">Alpha Companion</div><div className="text-[10px] text-[#64748b]">Your AI investing guide</div></div></div>
              <select data-testid="chat-provider" value={provider} onChange={(e) => setProvider(e.target.value)} className="bg-[#0f1420] border border-[#2d3c59] rounded px-2 py-1 text-xs text-[#94a3b8]">
                <option value="claude">Claude</option><option value="gpt">GPT</option><option value="gemini">Gemini</option>
              </select>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {msgs.length === 0 && (
                <div className="text-center mt-6">
                  <Sparkles className="w-7 h-7 text-[#00e5ff] mx-auto mb-2" />
                  <div className="text-sm text-[#94a3b8]">Ask me anything about investing, your portfolio, or any stock, ETF, bond or crypto.</div>
                  <div className="grid gap-2 mt-4">
                    {SUGGESTIONS.map((s) => <button key={s} data-testid={`chat-suggestion`} onClick={() => send(s)} className="text-left text-xs rounded-lg border border-[#2d3c59] px-3 py-2 text-[#94a3b8] hover:text-[#f8fafc] hover:border-[#00e5ff]/40 transition-colors">{s}</button>)}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#00e5ff] text-[#06121a] rounded-br-sm" : "bg-[#1a2338] text-[#e2e8f0] rounded-bl-sm"}`}>{m.content}</div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="bg-[#1a2338] rounded-2xl px-4 py-3 flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] live-dot" /><span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] live-dot" style={{ animationDelay: ".2s" }} /><span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] live-dot" style={{ animationDelay: ".4s" }} /></div></div>}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-[#1e293b]">
              <div className="flex items-center gap-2 rounded-xl border border-[#2d3c59] px-3 py-2">
                <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about investing…" className="bg-transparent outline-none text-sm w-full text-[#f8fafc] placeholder:text-[#64748b]" />
                <button data-testid="chat-send" onClick={() => send()} disabled={busy} className="text-[#00e5ff] disabled:opacity-40"><Send className="w-4 h-4" /></button>
              </div>
              <div className="text-[10px] text-[#64748b] mt-1.5 text-center">Educational only · not personalised investment advice</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
